import { Injectable } from '@angular/core';
import { BrowserService } from '@core/browser.service';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { PlannerModel } from '@ece-models/planner.model';
import { DragDropType } from '@shared/enums/drag-drop-type';
import { BehaviorSubject } from 'rxjs';
import { WeeklyPlannerService } from './weekly-planner.service';

export interface DragFormatStatus {
  source?: string;
  isDrag: boolean;
  containerId?: string;
  dragType: DragDropType,
}

export const defaultDragFormatStatus = {
  isDrag: false,
  source: undefined,
  containerId: undefined,
  dragType: undefined
};

@Injectable({
  providedIn: 'root'
})
export class DragFormatService implements StatefulService {
  /**
   * Used for lesson cards from dock and planner
   * Set draggedElement on drag start to allow Service to track the current element being dragged
   * Reset properties upon drag end
   */
  private draggedElement = null;
  private isDragFormatStatus = new BehaviorSubject<DragFormatStatus>(defaultDragFormatStatus);
  getIsDragFormatStatus = this.isDragFormatStatus.asObservable();
  private isSafariBrowser: boolean = null;
  private cursorXOffset: number = 0;
  private cursorYOffset: number = 0;
  planner$: BehaviorSubject<PlannerModel> = this.weeklyPlannerService.plannerData$;
  dragType: DragDropType = null;
  /**
   * Classes that are added to the draggedELement during drag by drag type
   * These are used with a global scope and should not be reused elsewhere
   */
  activeDragClassesByType = {
    [DragDropType.dockCard]: 'dock-card-drag-active',
    [DragDropType.plannerCard]: 'planner-card-drag-active',
  };

  constructor(private browserService: BrowserService,
    private weeklyPlannerService: WeeklyPlannerService,
  ) {
    this.isSafariBrowser = this.browserService.isSafari;
  }

  setIsDrag(
    isDrag: boolean, source: string | undefined = undefined,
    containerId: string | undefined = undefined, dragType: DragDropType = null
  ) {
    this.dragType = dragType;

    if (!isDrag) {
      this.removeDraggedElement();
      this.resetDraggedElementTracking();
    }

    // do not throw event if there is not group name
    if (containerId === undefined) {
      this.isDragFormatStatus.next(defaultDragFormatStatus);
      return;
    }
    this.isDragFormatStatus.next({ isDrag, source, containerId, dragType });
  }

  removeClassFromDraggedElement(className: string) {
    this.draggedElement?.classList?.remove(className);
  }

  addClassToDraggedElement(className: string) {
    this.draggedElement.classList.add(className);
  }

  /**
   * Sets up the dragged element's properties and styles.
  */
  setDraggedElement(event: DragEvent) {
    let draggedElement: HTMLElement = event.target as HTMLElement;

    if (draggedElement) {
      // Because touch can be grabbing a subelement we need to make sure we have the correct target
      if (!draggedElement.hasAttribute('data-draggable')) {
        draggedElement = draggedElement.closest('[data-draggable]');
      }

      const dragItemWidth = draggedElement.getBoundingClientRect().width;
      const dragItemHeight = draggedElement.getBoundingClientRect().height;

      // Offsets needed to position the cursor slightly below the middle of the dragged element
      this.cursorXOffset = dragItemWidth ? dragItemWidth  / 2 : 0;
      this.cursorYOffset = dragItemHeight ? dragItemHeight * .9 : 0;

      // Clones the click target element and updates its styles for drag
      this.draggedElement = draggedElement.cloneNode(true) as HTMLElement;

      if (this.dragType === DragDropType.dockCard) {
        this.setDockCardDragStyle(event, dragItemHeight, dragItemWidth);
      } else if (this.dragType === DragDropType.plannerCard) {
        this.setPlannerCardDragStyle(dragItemHeight, dragItemWidth);
      }

      this.updateBrowserWithElement(event);
    }
  }

  private setDockCardDragStyle(event: DragEvent, dragItemHeight: number, dragItemWidth: number) {
    const color = event.dataTransfer.getData('dockLessonCardDecorationColor');

    this.draggedElement.classList.add(this.activeDragClassesByType[DragDropType.dockCard]);
    this.draggedElement.style.height = `${dragItemHeight}px`;
    this.draggedElement.style.width = `${dragItemWidth}px`;
    this.draggedElement.style.borderLeftColor = `#${color}`;
    this.draggedElement.style.borderRightColor = `#${color}`;

    // We are not sent both the colors so make this more opaque to match the planner color
    this.draggedElement.style.backgroundColor = `#${color}40`;

    this.addDragCardBacking();
  }

  private setPlannerCardDragStyle(dragItemHeight: number, dragItemWidth: number) {
    this.draggedElement.classList.add(this.activeDragClassesByType[DragDropType.plannerCard]);
    this.draggedElement.style.width  = `${dragItemWidth}px`;
    this.draggedElement.style.height  = `${dragItemHeight}px`;
  }

  private addDragCardBacking() {
    const backingDiv = document.createElement('div');

    backingDiv.classList.add('dock-card-drag-active');
    backingDiv.style.width = this.draggedElement.style.width;
    backingDiv.style.height = this.draggedElement.style.height;
    backingDiv.appendChild(this.draggedElement);

    this.draggedElement = backingDiv;
  }

  /**
   * Positions the dragged element on the screen based on the provided coordinates.
   */
  positionDragElement(xCoordinate: number,  yCoordinate: number) {
    if (this.draggedElement) {
      // Handle the offset to adjust where element should be relative to cursor
      this.draggedElement.style.left = `${xCoordinate - this.cursorXOffset}px`;
      this.draggedElement.style.top = `${yCoordinate - this.cursorYOffset}px`;
    }
  }

  /**
   * Removes the cloned element that was added and dragged on the DOM
   */
  removeDraggedElement() {
    if (this.draggedElement) {
      this.draggedElement.style.display = 'none';
    }

    try {
      document.body.removeChild(this.draggedElement);
      this.resetDraggedElementTracking();
    } catch (err) {
      console.error('Error occurred in removeDraggedElement:', err);
      this.resetDraggedElementTracking();
    }
  }

  private resetDraggedElementTracking() {
    this.cursorXOffset = 0;
    this.cursorYOffset = 0;
    this.draggedElement = null;
    this.dragType = null;
  }

  /**
   * All browsers require the cloned element to be appended to body
   * Safari requires the cloned element to be set as the drag image
   * Chromium & Firefox browsers display a second element if drag image is set with cloned element or not set at all
   */
  private updateBrowserWithElement(event: DragEvent) {
    let elementForSetDragImage;
    if (!this.isSafariBrowser) {
      // Create a non-displayed element for the default dragged image
      const spanElement = document.createElement('span');
      spanElement.style.display = 'none';
      elementForSetDragImage = spanElement;
    } else {
      // Use the cloned element for the drag image
      elementForSetDragImage = this.draggedElement;
    }
    if (this.draggedElement && elementForSetDragImage) {
      event.dataTransfer.setDragImage(elementForSetDragImage, this.cursorXOffset, this.cursorYOffset);
      document.body.appendChild(this.draggedElement);
    }
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.draggedElement = null;
    this.isDragFormatStatus.next(defaultDragFormatStatus);
    this.cursorXOffset = 0;
    this.cursorYOffset = 0;
    this.dragType = null;
  }
}
